import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Send,
  CheckCircle,
  XCircle,
  ArrowUp,
  ArrowDown,
  RefreshCcw,
  Smartphone,
  Users,
  Calendar,
  Flag,
} from "lucide-react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import subscriptionRequests from "../../api/Customer/SubscriptionRequests"; // Adjust path as needed

const Summary = ({ userStats, currentUserSubscriptionData }) => {
  const navigate = useNavigate();
  const subscriptionPlan = useSelector(
    (state) => state.user?.txtconnect_ng_user?.subscription_plan
  );
  const user = useSelector((state) => state.user?.txtconnect_ng_user);
  const [summaryData, setSummaryData] = useState({
    totalBulkMessages: 0,
    smsSentToday: 0,
    smsDelivered: 0,
    smsFailed: 0,
    percentChange: {
      totalBulkMessages: 0,
      smsSentToday: 0,
      smsDelivered: 0,
      smsFailed: 0,
    },
  });

  const [quickStats, setQuickStats] = useState({
    smsBalance: 0, // Initialize as 0, will be fetched from API
    smsCount: 0,
    contactGroups: 0,
    totalContactsUploaded: 0,
  });

  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Fetch subscription data from API
 
  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const response = await subscriptionRequests.getCurrentUserSubscriptionData();
      
      if (response.status_code === "000" && response.data) {
        // Extract units from the API response
        // Adjust the property name based on your actual API response structure
        const unitsFromAPI = response.data?.units ??
                            response.data?.data?.units ??
                            response.remaining_units ??
                            response.sms_balance ??
                            0;
        console.log("unitsFromAPI:", unitsFromAPI);
        // Update quickStats with units from API
        setQuickStats(prev => ({
          ...prev,
          smsBalance: parseInt(unitsFromAPI, 10) || 0
        }));
      } else {
        console.error("Failed to fetch subscription data:", response.reason);
        // Fallback to Redux/local storage if API fails
        setQuickStats(prev => ({
          ...prev,
          smsBalance: parseInt(user?.units) || subscriptionPlan?.units || 0
        }));
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      // Fallback to Redux/local storage on error
      setQuickStats(prev => ({
        ...prev,
        smsBalance: parseInt(user?.units) || subscriptionPlan?.units || 0
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch subscription data on component mount
    fetchSubscriptionData();
  }, []);

  useEffect(() => {
    if (userStats) {
      const newSummaryData = {
        totalBulkMessages: userStats.total_bulk_messages_today || 0,
        smsSentToday: userStats.total_quick_sms_today || 0,
        smsDelivered: userStats.total_sms_sent || 0,
        smsFailed: userStats.sms_failed || 0,
        percentChange: {
          totalBulkMessages: 0,
          smsSentToday: 0,
          smsDelivered: 0,
          smsFailed: 0,
        },
      };
      setSummaryData(newSummaryData);
      
      // Update other stats but keep SMS balance from API
      setQuickStats(prev => ({
        ...prev,
        smsCount: userStats.total_sms_sent || 0,
        contactGroups: userStats.total_contact_groups || 0,
        totalContactsUploaded: parseInt(userStats.total_contacts_uploaded) || 0,
        // Don't update smsBalance here - it comes from API
      }));
    }
  }, [userStats, subscriptionPlan, user]);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        delay: custom * 0.1,
      },
    }),
    hover: {
      y: -5,
      boxShadow:
        "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      transition: { type: "spring", stiffness: 400 },
    },
  };

  const actionCardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: (custom) => ({
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        delay: custom * 0.1 + 0.3,
      },
    }),
    hover: {
      scale: 1.03,
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      transition: { type: "spring", stiffness: 400 },
    },
  };

  // Add a refresh button handler
  const handleRefreshSmsBalance = () => {
    fetchSubscriptionData();
  };

  return (
    <div className="px-4 pt-20 pb-12 mx-auto max-w-8xl sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between items-start mt-16 mb-8 sm:flex-row sm:items-center">
        <div>
          <h2 className="mb-2 text-2xl font-bold text-gray-800">
            Dashboard Overview
          </h2>
          <p className="text-gray-600">
            Track your messaging performance at a glance
          </p>
        </div>
        <div className="flex gap-2 items-center px-4 py-2 mt-4 text-red-700 bg-red-50 rounded-lg border border-red-200 sm:mt-0">
          <Flag className="w-4 h-4" />
          <span className="text-sm font-medium">Ghanaian SMS Provider</span>
        </div>
      </div>

      {/* Main Summary Cards */}
      <div className="grid grid-cols-1 gap-6 mb-12 md:grid-cols-2 lg:grid-cols-3">
        {/* Total Bulk Messages Card */}
        <motion.div
          custom={1}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          className="overflow-hidden relative p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border border-blue-200 shadow-md"
        >
          <div className="absolute top-0 right-0 -mt-8 -mr-8 w-24 h-24 bg-blue-400 rounded-full opacity-20"></div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex justify-center items-center p-3 bg-blue-400 rounded-full">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="mb-1 font-medium text-gray-700">
            Total Bulk Messages Sent Today
          </h3>
          <div className="flex items-baseline">
            <span className="text-4xl font-bold text-gray-800">
              {(summaryData.totalBulkMessages || 0).toLocaleString()}
            </span>
          </div>
          <div className="overflow-hidden mt-4 w-full h-1 bg-blue-200 rounded-full">
            <div
              className="h-full bg-blue-400 rounded-full"
              style={{ width: "85%" }}
            ></div>
          </div>
        </motion.div>

        {/* SMS Sent Today Card */}
        <motion.div
          custom={2}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          className="overflow-hidden relative p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl border border-orange-200 shadow-md"
        >
          <div className="absolute top-0 right-0 -mt-8 -mr-8 w-24 h-24 bg-orange-400 rounded-full opacity-20"></div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex justify-center items-center p-3 bg-orange-400 rounded-full">
              <Send className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="mb-1 font-medium text-gray-700"> Quick SMS Sent Today</h3>
          <div className="flex items-baseline">
            <span className="text-4xl font-bold text-gray-800">
              {(summaryData.smsSentToday || 0).toLocaleString()}
            </span>
          </div>
          <div className="overflow-hidden mt-4 w-full h-1 bg-orange-200 rounded-full">
            <div
              className="h-full bg-orange-400 rounded-full"
              style={{ width: "45%" }}
            ></div>
          </div>
        </motion.div>

        {/* SMS Delivered Card */}
        <motion.div
          custom={3}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          className="overflow-hidden relative p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border border-green-200 shadow-md"
        >
          <div className="absolute top-0 right-0 -mt-8 -mr-8 w-24 h-24 bg-green-400 rounded-full opacity-20"></div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex justify-center items-center p-3 bg-green-700 rounded-full">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="mb-1 font-medium text-gray-700">SMS Delivered</h3>
          <div className="flex items-baseline">
            <span className="text-4xl font-bold text-gray-800">
              {(summaryData.smsDelivered || 0).toLocaleString()}
            </span>
          </div>
          <div className="overflow-hidden mt-4 w-full h-1 bg-green-200 rounded-full">
            <div
              className="h-full bg-green-700 rounded-full"
              style={{ width: "92%" }}
            ></div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions Section */}
      <div className="mb-6">
        <h2 className="mb-2 text-2xl font-bold text-gray-600">Quick Actions</h2>
        <p className="text-gray-600">Send messages and manage your account</p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* SMS Balance Card - Now with API data */}
        <motion.div
          custom={1}
          variants={actionCardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          className="overflow-hidden relative p-6 bg-white rounded-xl border border-gray-100 shadow-md"
        >
          <div className="relative z-10">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-4xl font-extrabold text-gray-600">
                {loading ? (
                  <span className="inline-block w-20 h-10 bg-gray-200 rounded animate-pulse"></span>
                ) : (
                  quickStats.smsBalance
                )}
              </h3>
              <button
                onClick={handleRefreshSmsBalance}
                className="p-2 text-gray-400 rounded-full hover:bg-gray-100 hover:text-red-600 transition-colors"
                title="Refresh SMS Balance"
              >
                <RefreshCcw className="w-4 h-4" />
              </button>
            </div>
            <p className="mb-6 text-gray-600">SMS Balance (Live)</p>
            <div className="mt-auto">
              <button
                onClick={() => navigate("/subscription-plans")}
                className="flex gap-2 justify-center items-center px-4 py-3 w-full font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-red-200"
              >
                <RefreshCcw className="w-4 h-4" />
                <span>Buy More SMS Credit</span>
              </button>
            </div>
          </div>
          <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-red-100 rounded-full opacity-60"></div>
          <div className="absolute top-4 right-4">
            <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-full">
              <Smartphone className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </motion.div>

        {/* Contact Groups Card */}
        <motion.div
          custom={3}
          variants={actionCardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          className="overflow-hidden relative p-6 bg-white rounded-xl border border-gray-100 shadow-md"
        >
          <div className="relative z-10">
            <h3 className="mb-2 text-4xl font-extrabold text-gray-600">
              {quickStats.contactGroups}
            </h3>
            <p className="mb-6 text-gray-600">Contact Groups</p>
            <div className="mt-auto">
              <button
                onClick={() => navigate("/contacts/upload", {
                  state: { locationRoute: window.location.href }
                })}
                className="flex gap-2 justify-center items-center px-4 py-3 w-full font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-red-200"
              >
                <Users className="w-4 h-4" />
                <span>Upload Contacts</span>
              </button>
            </div>
          </div>
          <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-red-100 rounded-full opacity-60"></div>
          <div className="absolute top-4 right-4">
            <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-full">
              <Users className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </motion.div>

        {/* SMS Count Card */}
        <motion.div
          custom={2}
          variants={actionCardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          className="overflow-hidden relative p-6 bg-white rounded-xl border border-gray-100 shadow-md"
        >
          <div className="relative z-10">
            <h3 className="mb-2 text-4xl font-extrabold text-gray-600">
              {quickStats.smsCount}
            </h3>
            <p className="mb-6 text-gray-600">SMS Count</p>
            <div className="mt-auto">
              <button
                onClick={() => navigate("/quick-sms")}
                className="flex gap-2 justify-center items-center px-4 py-3 w-full font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-red-200"
              >
                <Send className="w-4 h-4" />
                <span>Send Quick SMS</span>
              </button>
            </div>
          </div>
          <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-red-100 rounded-full opacity-60"></div>
          <div className="absolute top-4 right-4">
            <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-full">
              <MessageSquare className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </motion.div>
        
        {/* Quick SMS Card */}
        <motion.div
          custom={4}
          variants={actionCardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          className="overflow-hidden relative p-6 bg-white rounded-xl border border-gray-100 shadow-md"
        >
          <div className="relative z-10">
            <h3 className="mb-2 text-4xl font-extrabold text-gray-600">
              {quickStats.totalContactsUploaded}
            </h3>
            <p className="mb-6 text-gray-600">View Contacts</p>
            <div className="mt-auto">
              <button
                onClick={() => navigate("/contacts")}
                className="flex gap-2 justify-center items-center px-4 py-3 w-full font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-red-200"
              >
                <Calendar className="w-4 h-4" />
                <span>Upload Contacts</span>
              </button>
            </div>
          </div>
          <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-red-100 rounded-full opacity-60"></div>
          <div className="absolute top-4 right-4">
            <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-full">
              <Calendar className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Summary;